/**
 * Tabs Component
 *
 * A tab navigation component with badge counts, icons, and RTL support.
 * Follows the existing ProfilePage tabs pattern.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface Tab {
  id: string;
  label: string;
  icon?: string;
  badge?: number | string;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: Tab[];
  activeTab?: string;
  onChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
}

export default function Tabs({
  tabs,
  activeTab,
  onChange,
  variant = 'default',
  size = 'md',
  fullWidth = false,
  className = '',
}: TabsProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [internalActive, setInternalActive] = useState(tabs[0]?.id || '');

  const currentTab = activeTab ?? internalActive;

  const handleTabClick = (tabId: string) => {
    if (onChange) {
      onChange(tabId);
    } else {
      setInternalActive(tabId);
    }
  };

  const baseClasses = 'flex items-center gap-2 font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/50';

  const variantClasses = {
    default: {
      container: 'flex gap-1 p-1 bg-gray-100 dark:bg-[var(--pv-color-surface-soft)] rounded-xl',
      tab: 'rounded-lg',
      active: 'bg-white dark:bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-text)] shadow-sm',
      inactive: 'text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-text)]',
    },
    pills: {
      container: 'flex gap-2',
      tab: 'rounded-full border border-transparent',
      active: 'bg-[var(--pv-color-primary)] text-white shadow-md',
      inactive: 'text-[var(--pv-color-text-muted)] border-[var(--pv-border-soft)] hover:border-[var(--pv-color-primary)] hover:text-[var(--pv-color-primary)]',
    },
    underline: {
      container: 'flex gap-4 border-b border-[var(--pv-border-soft)]',
      tab: 'border-b-2 border-transparent -mb-px',
      active: 'text-[var(--pv-color-primary)] border-[var(--pv-color-primary)]',
      inactive: 'text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-text)] hover:border-gray-300',
    },
  };

  const sizeClasses: Record<Required<TabsProps>['size'], string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  const styles = variantClasses[variant];

  return (
    <div
      className={`${styles.container} ${fullWidth ? 'w-full' : ''} ${className}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      role="tablist"
    >
      {tabs.map((tab) => {
        const isActive = currentTab === tab.id;
        const tabClasses = `
          ${baseClasses}
          ${styles.tab}
          ${sizeClasses[size]}
          ${isActive ? styles.active : styles.inactive}
          ${fullWidth ? 'flex-1 justify-center' : ''}
          ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled}
            className={tabClasses}
            onClick={() => !tab.disabled && handleTabClick(tab.id)}
            disabled={tab.disabled}
          >
            {tab.icon && <i className={`${tab.icon} text-base`}></i>}
            <span>{tab.label}</span>
            {tab.badge !== undefined && (
              <span
                className={`
                  inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium
                  ${isActive
                    ? variant === 'pills'
                      ? 'bg-white/20 text-white'
                      : 'bg-[var(--pv-color-primary)]/10 text-[var(--pv-color-primary)]'
                    : 'bg-gray-200 dark:bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]'
                  }
                `}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Tab panel wrapper for content
export function TabPanel({
  children,
  tabId,
  activeTab,
  className = '',
}: {
  children: ReactNode;
  tabId: string;
  activeTab: string;
  className?: string;
}) {
  if (tabId !== activeTab) return null;

  return (
    <div
      role="tabpanel"
      aria-labelledby={`tab-${tabId}`}
      className={`animate-fadeIn ${className}`}
    >
      {children}
    </div>
  );
}
